/**
 * Shared calendar projection math.
 * Used by CalendarView and the OnboardingScreen mini-calendar preview.
 */

export function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

export function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export function toKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Returns true if a budget item hits on the given year/month/day. */
export function itemHitsOnDate(item, year, month, day) {
  const date = new Date(year, month, day);
  const lastOcc = item.lastOccurrence ? new Date(item.lastOccurrence + 'T00:00:00') : null;
  switch (item.frequency) {
    case 'Daily': return true;
    case 'Weekly': {
      if (lastOcc) { const diff = Math.round((date - lastOcc) / 86400000); return diff % 7 === 0; }
      return date.getDay() === 1;
    }
    case 'Biweekly': {
      if (lastOcc) { const diff = Math.round((date - lastOcc) / 86400000); return diff % 14 === 0; }
      const anchor = new Date(year, 0, 1);
      while (anchor.getDay() !== 1) anchor.setDate(anchor.getDate() + 1);
      const diff = Math.round((date - anchor) / 86400000);
      return date.getDay() === 1 && diff % 14 === 0;
    }
    case 'Monthly': {
      if (lastOcc) return day === lastOcc.getDate();
      return day === (item.dayOfMonth || 1);
    }
    case 'Quarterly': {
      if (lastOcc) {
        const md = (year - lastOcc.getFullYear()) * 12 + (month - lastOcc.getMonth());
        return md % 3 === 0 && day === lastOcc.getDate();
      }
      return day === (item.dayOfMonth || 1) && [0, 3, 6, 9].includes(month);
    }
    case 'Semi-annually': {
      if (lastOcc) {
        const md = (year - lastOcc.getFullYear()) * 12 + (month - lastOcc.getMonth());
        return md % 6 === 0 && day === lastOcc.getDate();
      }
      return day === (item.dayOfMonth || 1) && [0, 6].includes(month);
    }
    case 'Yearly': {
      if (lastOcc) return month === lastOcc.getMonth() && day === lastOcc.getDate();
      return day === (item.dayOfMonth || 1) && month === 0;
    }
    default: return false;
  }
}

/** Returns the NET recurring amount for a given day (signed: + income, - expense). */
export function getRecurringAmount(budgetItems, year, month, day, skippedIds = []) {
  return budgetItems.reduce((total, item) => {
    if (skippedIds.includes(item.id)) return total;
    if (!itemHitsOnDate(item, year, month, day)) return total;
    return total + (item.type === 'income' ? item.amount : -item.amount);
  }, 0);
}

/** Returns all budget items that hit on the given day, excluding any skipped for that day. */
export function getRecurringItemsForDay(budgetItems, year, month, day, skippedIds = []) {
  return budgetItems.filter(
    (item) => !skippedIds.includes(item.id) && itemHitsOnDate(item, year, month, day)
  );
}

/** Calculate the projected end-of-day balance for a given date, using today's balance as anchor. */
export function getDayBalance(year, month, day, endOfDayBalance, budgetItems, oneOffTransactions = {}, skippedOccurrences = {}) {
  const todayObj = new Date();
  todayObj.setHours(0, 0, 0, 0);
  const target = new Date(year, month, day);

  let balance = endOfDayBalance;

  if (target > todayObj) {
    let cursor = new Date(todayObj.getTime() + 86400000);
    while (cursor <= target) {
      const y = cursor.getFullYear();
      const m = cursor.getMonth();
      const d = cursor.getDate();
      const key = toKey(y, m, d);
      const oneOffs = (oneOffTransactions[key] || []).reduce((s, t) => s + t.amount, 0);
      const recurring = getRecurringAmount(budgetItems, y, m, d, skippedOccurrences[key] || []);
      balance += oneOffs + recurring;
      cursor = new Date(cursor.getTime() + 86400000);
    }
  } else if (target < todayObj) {
    let cursor = new Date(todayObj);
    while (cursor > target) {
      const y = cursor.getFullYear();
      const m = cursor.getMonth();
      const d = cursor.getDate();
      const key = toKey(y, m, d);
      const oneOffs = (oneOffTransactions[key] || []).reduce((s, t) => s + t.amount, 0);
      const recurring = getRecurringAmount(budgetItems, y, m, d, skippedOccurrences[key] || []);
      balance -= oneOffs + recurring;
      cursor = new Date(cursor.getTime() - 86400000);
    }
  }

  return balance;
}
